// Lib
import React, { useState } from 'react';
import { kebabCase } from 'lodash/fp';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

// Components
import Tool from '../Tool';
import Icon from '../../../../components/icons/Icon';
import TooltipSource from '../../../../components/tooltips/TooltipSource';
import DraggableElementCreationTool from '../../../../element/dnd/DraggableElementCreationTool';

// Utils
import { getDefaultElementOffset, getMobileElementOffset } from '../../../../utils/dnd/dragPositionUtils';
import * as pointLib from '../../../../../common/maths/geometry/point';
import { getScaledGrabOffset } from '../../../../utils/dnd/dragAndDropUtils';
import { selectIsMobileAppMode } from '../../../../platform/platformSelector';

// Constants
import { TooltipPositions } from '../../../../components/tooltips/tooltipConstants';
import { TIMES } from '../../../../../common/utils/timeUtil';
import { ELEMENT_CREATION_SOURCES } from '../../../../../common/elements/elementConstants';

// Types
import { MNElement } from '../../../../../common/elements/elementModelTypes';
import { Point } from '../../../../../common/maths/geometry/pointTypes';
import { DragSourceMonitor } from 'react-dnd';
import { ElementType } from '../../../../../common/elements/elementTypes';

// Styles
import './ElementTool.scss';

type ElementToolProps = {
    name: string;
    toolName: string;
    elementType: ElementType;
    currentBoardId: string;
    currentUserId: string;
    gridSize: number;
    isDragging?: boolean;
    selectOnCreate?: boolean;
    editOnCreate?: boolean;
    focusInputEditorType?: string;
    icon?: JSX.Element;
    iconName?: string;
    connectDragSource?: (element: JSX.Element) => JSX.Element;
    connectDragPreview?: (element: JSX.Element) => JSX.Element;
    getDefaultContent?: () => any;
    onDragEnd?: () => void;
};

/**
 * Retrieves the drag or drop position for an element.
 * Usually this is simply the "source client offset" (the top left point of the element being dragged), however if a
 * board is dragged out of a list it changes shape, so it needs to track the position of the mouse instead (the client
 * offset).
 *
 * Documentation: https://app.milanote.com/1OV90S1okndh8V?p=jx9OIrFgQsG
 */
const getElementScaledCustomDragOffset =
    (getDefaultOffset: (args: { element: MNElement; gridSize: number }) => Point) =>
    ({ gridSize }: { gridSize: number }, monitor: DragSourceMonitor, element: MNElement) => {
        const sourceClientOffset = monitor.getSourceClientOffset();
        const clientOffset = monitor.getClientOffset();

        const scaledGrabOffset = getScaledGrabOffset({ clientOffset, sourceClientOffset });

        const defaultOffset = getDefaultOffset({ element, gridSize });

        // @ts-ignore - lodash curry :(
        return pointLib.translate(defaultOffset, scaledGrabOffset);
    };

const getElementScaledGrabOffset = ({ gridSize }: ElementToolProps, monitor: DragSourceMonitor, element: MNElement) => {
    return pointLib.scale(-1, getDefaultElementOffset({ element, gridSize }));
};

const getMobileElementScaledDragOffset = (
    { gridSize }: ElementToolProps,
    monitor: DragSourceMonitor,
    element: MNElement,
) => {
    return pointLib.scale(-1, getMobileElementOffset({ element, gridSize }));
};

const DESKTOP_TOOL_OPTIONS = {
    getElementScaledGrabOffset,
    getElementScaledCustomDragOffset: getElementScaledCustomDragOffset(getDefaultElementOffset),
    creationSource: ELEMENT_CREATION_SOURCES.TOOLBAR,
};

const MOBILE_TOOL_OPTIONS = {
    getElementScaledGrabOffset: getMobileElementScaledDragOffset,
    getElementScaledCustomDragOffset: getElementScaledCustomDragOffset(getMobileElementOffset),
    // 90 is upwards, so we allow a 20 degree range either side of that
    dragAngleRanges: [{ start: 90 - 20, end: 90 + 20 }],
    creationSource: ELEMENT_CREATION_SOURCES.TOOLBAR,
};

const ElementTool = (props: ElementToolProps) => {
    const { toolName, icon, iconName } = props;

    const [animationClassName, setAnimationClassName] = useState('');
    const isMobileAppMode = useSelector(selectIsMobileAppMode);

    const toolOptions = isMobileAppMode ? MOBILE_TOOL_OPTIONS : DESKTOP_TOOL_OPTIONS;
    const tooltipPosition = isMobileAppMode ? TooltipPositions.TOP : TooltipPositions.RIGHT;

    const handleOnToolClick = () => {
        const animationClass = isMobileAppMode ? 'bounce-animation-mobile' : 'bounce-animation-desktop';

        setAnimationClassName(animationClass);
        setTimeout(() => setAnimationClassName(''), TIMES.SECOND * 0.4);
    };

    return (
        <div
            className={`ElementToolDraggable element-tool-${kebabCase(toolName)}`}
            id={`element-tool-${kebabCase(toolName)}`}
        >
            {/* @ts-ignore - TODO: Fix proptypes */}
            <DraggableElementCreationTool {...props} {...toolOptions}>
                <TooltipSource
                    tooltipText="Drag me"
                    position={tooltipPosition}
                    pollPosition
                    distance={12}
                    duration={TIMES.SECOND * 5}
                    delay={TIMES.SECOND * 0.4}
                    hideOnEvent="pointerdown"
                    forceDelay
                    enabled
                    fade
                    triggerOnClick
                >
                    <Tool
                        {...props}
                        className={classNames('ElementTool', `element-tool-${kebabCase(toolName)}`, animationClassName)}
                        isDraggable
                        onClick={handleOnToolClick}
                    >
                        {icon || <Icon name={iconName || `element-${kebabCase(toolName)}`} />}
                    </Tool>
                </TooltipSource>
            </DraggableElementCreationTool>
        </div>
    );
};

export default ElementTool;
